import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { FilterSpec, Pageable, WrapperPageableResponse, WrapperResponse } from './service-common';
import { TaxDTO } from './tax.service';

export type TaxCollectionType = 'SELLER_COLLECTS' | 'SELLER_WITHHOLDS';

export interface ProductTaxDTO {
  id: number;
  tax: TaxDTO;
  taxRate?: number;
  taxAmount?: number;
  applicationType?: 'TAXABLE' | 'EXEMPT' | 'ZERO_RATED' | 'REVERSE_CHARGE';
  collectionType?: TaxCollectionType;
  createdAt?: string;
  updatedAt?: string;
  state?: 'ACTIVE' | 'INACTIVE';
}

export interface ProductTaxFilterSpec extends FilterSpec {}

export type WrapperPageableResponseProductTaxDTO = WrapperPageableResponse<ProductTaxDTO>;
export type WrapperResponseProductTaxDTO = WrapperResponse<ProductTaxDTO>;

export interface CreateProductTaxRequest {
  taxId: number;
  taxRate?: number;
  taxAmount?: number;
  applicationType: 'TAXABLE' | 'EXEMPT' | 'ZERO_RATED' | 'REVERSE_CHARGE';
  collectionType?: TaxCollectionType;
}

export interface UpdateProductTaxRequest extends CreateProductTaxRequest {}

@Injectable({
  providedIn: 'root'
})
export class ProductTaxService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getProductTaxes(
    spec: ProductTaxFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseProductTaxDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            params = params.append(key, String(item));
          }
        });
        return;
      }
      params = params.append(key, String(value));
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort?.length) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseProductTaxDTO>(`${this.apiUrl}/product-taxes`, { params });
  }

  listProductTaxes(
    spec: ProductTaxFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseProductTaxDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort?.length) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseProductTaxDTO>(`${this.apiUrl}/product-taxes/list`, { params });
  }

  getProductTaxById(productTaxId: number): Observable<WrapperResponseProductTaxDTO> {
    return this.http.get<WrapperResponseProductTaxDTO>(`${this.apiUrl}/product-taxes/${productTaxId}`);
  }

  createProductTax(request: CreateProductTaxRequest): Observable<WrapperResponseProductTaxDTO> {
    return this.http.post<WrapperResponseProductTaxDTO>(`${this.apiUrl}/product-taxes`, request);
  }

  updateProductTax(productTaxId: number, request: UpdateProductTaxRequest): Observable<WrapperResponseProductTaxDTO> {
    return this.http.put<WrapperResponseProductTaxDTO>(`${this.apiUrl}/product-taxes/${productTaxId}`, request);
  }

  deleteProductTax(productTaxId: number): Observable<WrapperResponseProductTaxDTO> {
    return this.http.delete<WrapperResponseProductTaxDTO>(`${this.apiUrl}/product-taxes/${productTaxId}`);
  }
}
